import { useState } from "react";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import figure1Data from "../../data/figure1c_aoa.json";
import importerMap from "../../data/importer_map.json";
import { computeSquareLayout } from "./layout";

interface VisualizationProps {
  currentStep: string;
  innerWidth: number;
  innerHeight: number;
  margins: { top: number; right: number; bottom: number; left: number };
}

const palette = {
  "not-reported": "#efefef",
  "both-reported-aligned": "#2d8f5cff",
  "both-reported-misaligned": "#85d29cff",
  "importer-only": "#eff163ff",
  "exporter-only": "#45c2efff",
};

const textPalette = {
  "not-reported": "#797979ff",
  "both-reported-aligned": "#2d8f5cff",
  "both-reported-misaligned": "#67bc81ff",
  "importer-only": "#b9bc1bff",
  "exporter-only": "#45c2efff",
};

// Pre-compute country names from static import
const countryNames = (() => {
  const entries = Object.entries(importerMap).map(([k, v]) => [
    parseFloat(k as string),
    String(v),
  ]) as [number, string][];
  entries.sort((a, b) => a[0] - b[0]);
  return entries.map(([, v]) => v);
})();

export default function Figure1({
  currentStep,
  innerWidth,
  innerHeight,
  margins,
}: VisualizationProps) {
  const grid = figure1Data;

  const [hoverState, setHoverState] = useState<{
    id: string | null;
    anchorEl: Element | null;
    text: string;
    label: string;
    labelColor: string;
  }>({
    id: null,
    anchorEl: null,
    text: "",
    label: "",
    labelColor: palette["not-reported"],
  });

  function getColor(currentStep: string, value: number, r: number, c: number) {
    const valueMap = [
      "not-reported",
      "both-reported-aligned",
      "both-reported-misaligned",
      "importer-only",
      "exporter-only",
    ] as const;
    const colorKey = valueMap[value] ?? "not-reported";

    if (currentStep !== "ideal" && value === 0) {
      return "#ffffff";
    }
    switch (currentStep) {
      case "":
        if (r === c) {
          return "white";
        } else {
          return palette["both-reported-aligned"];
        }

      case "ideal":
        if (r === c) {
          return "white";
        } else {
          return palette["both-reported-aligned"];
        }

      case "actual":
        return palette[colorKey];

      case "reality-both-reported-aligned":
        if (value === 1) {
          return palette["both-reported-aligned"];
        } else {
          return palette["not-reported"];
        }
      case "reality-add-both-misaligned":
        if (value === 1) {
          return palette["both-reported-aligned"];
        } else if (value === 2) {
          return palette["both-reported-misaligned"];
        } else {
          return palette["not-reported"];
        }

      case "reality-add-importer-only":
        if (value === 1) {
          return palette["both-reported-aligned"];
        } else if (value === 2) {
          return palette["both-reported-misaligned"];
        } else if (value === 3) {
          return palette["importer-only"];
        } else {
          return palette["not-reported"];
        }

      case "reality-add-exporter-only":
        return palette[colorKey];

      case "reality":
        return palette[colorKey];

      default:
        return palette["not-reported"];
    }
  }

  const rows = grid.length;
  const cols = rows > 0 ? grid[0].length : 0;
  if (rows === 0 || cols === 0) return <g />;
  const { cellW, cellH, offsetX, offsetY } = computeSquareLayout(
    innerWidth,
    innerHeight,
    margins,
    5,
    rows,
    cols,
  );

  const groupOpacity = currentStep !== "combined-annotation" ? 1 : 0.15;

  const cells: React.ReactElement[] = [];
  for (let r = 0; r < rows; r++) {
    const row = grid[r];
    for (let c = 0; c < cols; c++) {
      const val = row[c];
      const x = offsetX + (cols - 1 - c) * cellW;
      const y = offsetY + r * cellH;
      const label =
        val === 0
          ? "No data"
          : val === 1
            ? "<25% discrepancy"
            : val === 2
              ? ">25% discrepancy"
              : val === 3
                ? "Only importer reported"
                : "Only exporter reported";
      cells.push(
        <rect
          key={`${r}-${c}`}
          id={`cell-${r}-${c}`}
          x={x}
          y={y}
          width={cellW}
          height={cellH}
          fill={getColor(currentStep, val, r, c)}
          stroke={hoverState.id === `cell-${r}-${c}` ? "#000" : "none"}
          strokeWidth={hoverState.id === `cell-${r}-${c}` ? 1 : 0}
          onMouseEnter={(e) => {
            const rowName = countryNames[r] ?? `Row ${r + 1}`;
            const colName = countryNames[c] ?? `Col ${c + 1}`;
            const valueMap = [
              "not-reported",
              "both-reported-aligned",
              "both-reported-misaligned",
              "importer-only",
              "exporter-only",
            ] as const;
            const colorKey = valueMap[val] ?? "not-reported";
            setHoverState({
              id: `cell-${r}-${c}`,
              anchorEl: e.currentTarget as Element,
              text: `Exporter: ${rowName}\nImporter: ${colName}`,
              label,
              labelColor: textPalette[colorKey],
            });
          }}
          onMouseLeave={() => {
            setHoverState((prev) => ({
              ...prev,
              id: null,
              anchorEl: null,
            }));
          }}
        />,
      );
    }
  }

  return (
    <>
      <g opacity={groupOpacity}>{cells}</g>
      <Popper
        open={Boolean(hoverState.anchorEl)}
        anchorEl={hoverState.anchorEl}
        placement="right"
        modifiers={[{ name: "offset", options: { offset: [8, 0] } }]}
        disablePortal={false}
        sx={{
          pointerEvents: "none",
          zIndex: (theme) => theme.zIndex.tooltip + 1,
        }}
      >
        <Paper elevation={1} sx={{ px: 1, py: 0.5 }}>
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.05,
            }}
          >
            {hoverState.text.split("\n").map((line, i) => (
              <div key={i} style={{ margin: 0, padding: 0 }}>
                {line}
              </div>
            ))}
            {hoverState.label && (
              <div
                style={{
                  margin: 0,
                  padding: 0,
                  color: hoverState.labelColor,
                  fontWeight: 600,
                }}
              >
                {hoverState.label}
              </div>
            )}
          </div>
        </Paper>
      </Popper>
    </>
  );
}
